import { Document, Page, View, Text } from '@react-pdf/renderer';
import type { WindowConfig, CurtainStyleConfig, FabricConfig, CutCalculation, PanelCut } from '@/types/curtain';
import { baseStyles, BRAND, fmtMm, fmtM, generateOrderNumber } from './pdfStyles';
import { getLabel } from '@/lib/enumLabels';

interface CutSheetPDFProps {
  customerName: string;
  windowConfig: Partial<WindowConfig>;
  styleConfig: Partial<CurtainStyleConfig>;
  fabricConfig: Partial<FabricConfig>;
  calculation: CutCalculation;
}

/** Returns heading preparation instructions based on heading style */
function getHeadingPrep(headingStyle: string): string {
  switch (headingStyle) {
    case 's-fold':
      return 'Apply S-Fold carrier tape. Ensure even spacing between glide hooks. Verify wave pitch matches track specification.';
    case 'pinch-pleat':
      return 'Apply 150mm buckram/header stiffener. Form triple pinch pleats at marked intervals. Hand-stitch pleat bases securely.';
    case 'eyelet':
      return 'Apply eyelet header tape. Position and press eyelet rings at equal intervals (typically 160–180mm centres). Ensure all eyelets face same direction.';
    case 'pencil-pleat':
      return 'Apply pencil pleat header tape (75–100mm). Draw up cords evenly to required finished width. Secure cord ends, do not trim.';
    default:
      return 'Standard heading preparation as per specification.';
  }
}

export function CutSheetPDF(props: CutSheetPDFProps) {
  const { customerName, windowConfig, styleConfig, fabricConfig, calculation } = props;

  const orderNumber = generateOrderNumber();
  const today = new Date();
  const headingStyle = styleConfig.headingStyle || 's-fold';
  const windowRef = `${windowConfig.roomName || 'Room'} — ${windowConfig.windowName || 'Window'}`;

  return (
    <Document title={`Cut Sheet - ${orderNumber}`} author="Hail Mary Curtains">
      <Page size="A4" style={baseStyles.page}>
        {/* Header */}
        <View style={baseStyles.headerRow}>
          <View>
            <Text style={baseStyles.brandName}>Hail Mary Curtains</Text>
            <Text style={{ fontSize: 8, color: BRAND.slate500, marginTop: 2 }}>Workroom Production Sheet</Text>
          </View>
          <View>
            <Text style={[baseStyles.docTitle, { color: BRAND.navy }]}>WORKROOM CUT SHEET</Text>
            <Text style={baseStyles.metaText}>Ref: {orderNumber}</Text>
            <Text style={baseStyles.metaText}>Date: {today.toLocaleDateString()}</Text>
          </View>
        </View>

        {/* Reference Info */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12 }}>
          <View style={{ width: '48%' }}>
            <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: BRAND.navy, marginBottom: 3 }}>CUSTOMER:</Text>
            <Text style={{ fontSize: 9, color: BRAND.slate700 }}>{customerName}</Text>
          </View>
          <View style={{ width: '48%' }}>
            <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: BRAND.navy, marginBottom: 3 }}>WINDOW REF:</Text>
            <Text style={{ fontSize: 9, color: BRAND.slate700 }}>{windowRef}</Text>
          </View>
        </View>

        {/* Key Measurements */}
        <Text style={baseStyles.sectionTitle}>Key Measurements</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          <View style={{ width: '33%', marginBottom: 8 }}>
            <Text style={{ fontSize: 7, color: BRAND.slate500 }}>Track Width</Text>
            <Text style={{ fontSize: 12, fontFamily: 'Helvetica-Bold', color: BRAND.navy }}>{fmtMm(windowConfig.trackWidth || 0)}</Text>
          </View>
          <View style={{ width: '33%', marginBottom: 8 }}>
            <Text style={{ fontSize: 7, color: BRAND.slate500 }}>Finished Drop</Text>
            <Text style={{ fontSize: 12, fontFamily: 'Helvetica-Bold', color: BRAND.navy }}>{fmtMm(windowConfig.finishedDrop || 0)}</Text>
          </View>
          <View style={{ width: '33%', marginBottom: 8 }}>
            <Text style={{ fontSize: 7, color: BRAND.slate500 }}>Total Fabric Width</Text>
            <Text style={{ fontSize: 12, fontFamily: 'Helvetica-Bold', color: BRAND.navy }}>{fmtMm(calculation.totalFabricWidth)}</Text>
          </View>
          <View style={{ width: '33%', marginBottom: 8 }}>
            <Text style={{ fontSize: 7, color: BRAND.slate500 }}>Cut Drop (per width)</Text>
            <Text style={{ fontSize: 12, fontFamily: 'Helvetica-Bold', color: BRAND.amber500 }}>{fmtMm(calculation.cutDrop)}</Text>
          </View>
          <View style={{ width: '33%', marginBottom: 8 }}>
            <Text style={{ fontSize: 7, color: BRAND.slate500 }}>Heading Allowance</Text>
            <Text style={{ fontSize: 12, fontFamily: 'Helvetica-Bold', color: BRAND.navy }}>{fmtMm(calculation.headingAllowance)}</Text>
          </View>
          <View style={{ width: '33%', marginBottom: 8 }}>
            <Text style={{ fontSize: 7, color: BRAND.slate500 }}>Hem Allowance</Text>
            <Text style={{ fontSize: 12, fontFamily: 'Helvetica-Bold', color: BRAND.navy }}>{fmtMm(calculation.hemAllowance)}</Text>
          </View>
        </View>

        {/* Fabric Specification */}
        <Text style={baseStyles.sectionTitle}>Fabric Specification</Text>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Fabric Code:</Text>
          <Text style={[baseStyles.kvValue, { fontFamily: 'Helvetica-Bold' }]}>{fabricConfig.fabricCode || 'CUSTOM'}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Roll Width (usable):</Text>
          <Text style={baseStyles.kvValue}>{fmtMm(fabricConfig.fabricWidth || 1400)}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Vertical Pattern Repeat:</Text>
          <Text style={baseStyles.kvValue}>{fabricConfig.verticalRepeat ? fmtMm(fabricConfig.verticalRepeat) : 'PLAIN (no repeat)'}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Pattern Adjustment Added:</Text>
          <Text style={baseStyles.kvValue}>{fmtMm(calculation.patternAdjustment)}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Total Fabric Required:</Text>
          <Text style={[baseStyles.kvValue, { fontFamily: 'Helvetica-Bold', color: BRAND.navy }]}>{fmtM(calculation.totalFabricMeters)}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Number of Widths:</Text>
          <Text style={[baseStyles.kvValue, { fontFamily: 'Helvetica-Bold', color: BRAND.navy }]}>{calculation.numberOfWidths}</Text>
        </View>

        {/* Panel Cutting Schedule */}
        <Text style={baseStyles.sectionTitle}>Panel Cutting Schedule</Text>
        <View style={baseStyles.tableHeader}>
          <Text style={[baseStyles.tableHeaderCell, { width: '12%' }]}>#</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '20%' }]}>Cut Drop</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '20%' }]}>Fin. Width</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '18%' }]}>Seam Allow.</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '15%' }]}>Pattern Ofs.</Text>
          <Text style={[baseStyles.tableHeaderCell, { width: '15%' }]}>Half Width</Text>
        </View>
        {calculation.panelBreakdown.map((panel: PanelCut, idx: number) => (
          <View key={panel.panelNumber} style={idx % 2 === 0 ? baseStyles.tableRow : baseStyles.tableRowAlt}>
            <Text style={[baseStyles.tableCell, { width: '12%', fontFamily: 'Helvetica-Bold' }]}>P{panel.panelNumber}</Text>
            <Text style={[baseStyles.tableCell, { width: '20%' }]}>{fmtMm(panel.cutDrop)}</Text>
            <Text style={[baseStyles.tableCell, { width: '20%' }]}>{fmtMm(panel.finishedWidth)}</Text>
            <Text style={[baseStyles.tableCell, { width: '18%' }]}>{fmtMm(panel.seamAllowance)} /side</Text>
            <Text style={[baseStyles.tableCell, { width: '15%' }]}>{fmtMm(panel.patternStartOffset)}</Text>
            <Text style={[baseStyles.tableCell, { width: '15%' }]}>{panel.isHalfWidth ? 'YES' : '—'}</Text>
          </View>
        ))}

        {/* Heading Preparation */}
        <Text style={baseStyles.sectionTitle}>Heading Preparation</Text>
        <View style={{ padding: 8, backgroundColor: BRAND.slate100, borderRadius: 4, marginBottom: 4 }}>
          <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold', color: BRAND.navy, marginBottom: 4 }}>
            {getLabel('HeadingStyle', headingStyle).toUpperCase()} — Instructions:
          </Text>
          <Text style={{ fontSize: 8, color: BRAND.slate700, lineHeight: 1.5 }}>
            {getHeadingPrep(headingStyle)}
          </Text>
        </View>

        {/* Hem & Finishing */}
        <Text style={baseStyles.sectionTitle}>Hem & Finishing</Text>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Bottom Hem (double fold):</Text>
          <Text style={[baseStyles.kvValue, { fontFamily: 'Helvetica-Bold' }]}>{fmtMm(calculation.hemAllowance)}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Side Hems:</Text>
          <Text style={baseStyles.kvValue}>25mm double fold each side</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Drop Style:</Text>
          <Text style={baseStyles.kvValue}>{getLabel('DropStyle', windowConfig.dropStyle || 'floating').toUpperCase()}</Text>
        </View>
        <View style={baseStyles.kvRow}>
          <Text style={baseStyles.kvLabel}>Mount Type:</Text>
          <Text style={baseStyles.kvValue}>{getLabel('MountType', windowConfig.mountType || 'wall').toUpperCase()}</Text>
        </View>

        {/* Workroom Notes */}
        <View style={[baseStyles.noteBox, { marginTop: 16 }]}>
          <Text style={[baseStyles.noteText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>WORKROOM CHECKLIST:</Text>
          <Text style={baseStyles.noteText}>☐ Verify fabric code and dye lot match PO</Text>
          <Text style={baseStyles.noteText}>☐ Check pattern repeat alignment across all panels</Text>
          <Text style={baseStyles.noteText}>☐ Confirm cut drop measurements before first cut</Text>
          <Text style={baseStyles.noteText}>☐ Mark seam joining sequence (P1→P{calculation.numberOfWidths})</Text>
          <Text style={baseStyles.noteText}>☐ Press and quality-check finished product</Text>
        </View>

        {/* Footer */}
        <View style={baseStyles.footer} fixed>
          <Text>Hail Mary Curtains — Workroom Cut Sheet</Text>
          <Text>Ref: {orderNumber}</Text>
        </View>
      </Page>
    </Document>
  );
}
